import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Check,
  Download,
  ExternalLink,
  Globe,
  Eye,
  Clock,
  Copy,
  CheckCheck,
  Rocket,
  Terminal,
} from 'lucide-react';
import { api, ApiError } from '../api/client';
import { useToast } from '../components/Toast';
import { PromoteDialog } from './PromoteDialog';
import { EjectDialog } from './EjectDialog';
import { cloneCommand } from '../lib/eject';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/Card';
import { StatusBadge } from '../components/StatusBadge';
import { BuildProgress } from '../components/BuildProgress';
import { IndexButton } from '../components/IndexButton';
import { formatRelative } from '../components/SiteCard';
import { LogoCard } from '../components/LogoCard';
import { Skeleton } from '../components/ui/Skeleton';
import { cn } from '../lib/cn';

interface Props {
  projectId: string;
  onBack: () => void;
  onGoToDomains: () => void;
}

export function ProjectDetailScreen({ projectId, onBack, onGoToDomains }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [ejectOpen, setEjectOpen] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const { data: state, isLoading } = useQuery({
    queryKey: ['projectState', projectId],
    queryFn: () => api.getProjectState(projectId),
    refetchInterval: (q) => {
      const s = q.state.data?.buildRun?.status;
      return s === 'running' || s === 'queued' ? 2000 : 10000;
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-8 w-72" />
        <div className="bg-white border border-zinc-200 rounded-xl p-6 mt-2">
          <Skeleton className="h-4 w-40 mb-3" />
          <Skeleton className="h-3 w-full mb-2" />
          <Skeleton className="h-3 w-3/4" />
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center">
        <h2 className="font-semibold text-zinc-900">Site not found</h2>
        <p className="text-[13px] text-zinc-500 mt-1">It may have been deleted.</p>
        <Button onClick={onBack} variant="outline" className="mt-4">
          Back to sites
        </Button>
      </div>
    );
  }

  const br = state.buildRun;
  const buildIsActive = br?.status === 'running' || br?.status === 'queued';
  const isMetadataOnly = br?.renderMode === 'metadata_only';
  const liveUrl =
    state.liveUrl ?? `https://${state.project.brandId}.sites.siteskick.com/`;
  const version = state.buildRunCount ?? undefined;
  const buildIsDone = br?.status === 'completed';
  const accepted =
    state.project.acceptedAt != null || state.project.autoAccept === true;
  const onPagesSubdomain =
    !state.liveUrl ||
    /\.pages\.dev\/?$/i.test(state.liveUrl) ||
    /\.sites\.[a-z0-9-]+\.[a-z]{2,}\/?$/i.test(state.liveUrl);
  const needsCustomDomain = buildIsDone && accepted && onPagesSubdomain;
  const isReallyLive = buildIsDone && !onPagesSubdomain && !!state.project.liveDeploymentId;

  async function handleAccept(): Promise<void> {
    setAccepting(true);
    try {
      await api.acceptProject(projectId);
      toast.success('Site approved — you can now connect a custom domain');
      qc.invalidateQueries({ queryKey: ['projectState', projectId] });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      toast.error(msg);
    } finally {
      setAccepting(false);
    }
  }

  return (
    <div className="flex flex-col gap-5 sm:gap-6">
      <div>
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1 text-[12.5px] text-zinc-500 hover:text-zinc-900 -ml-1 px-1 py-0.5 rounded transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All sites
        </button>
      </div>

      {/* Page header */}
      <header className="flex flex-col-reverse sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h1 className="text-2xl sm:text-[26px] font-semibold tracking-tight2 text-zinc-900 break-words">
              {state.project.displayName || state.project.brandId}
            </h1>
            <StatusBadge status={br?.status} hasLiveUrl={!!state.liveUrl} />
            {state.project.ejectedAt && (
              <span className="text-[10.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-600 text-white">
                Ejected
              </span>
            )}
          </div>
          <p className="text-[13px] text-zinc-500">
            {state.project.kitId} · created{' '}
            {new Date(state.project.createdAt).toLocaleDateString()}
            {version != null && ` · ${version} build${version === 1 ? '' : 's'}`}
          </p>
        </div>
        {isReallyLive && (
          <div className="shrink-0">
            <IndexButton projectId={projectId} buildRun={state.buildRun} />
          </div>
        )}
      </header>

      {/* Live site card */}
      {state.project.liveDeploymentId && (
        <Card className="overflow-hidden">
          <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="text-[11.5px] uppercase tracking-wide text-zinc-400 mb-1">
                Your site
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <Globe className="h-4 w-4 text-zinc-400 shrink-0" />
                <a
                  href={liveUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-zinc-900 hover:underline font-medium text-[16px] sm:text-[17px] truncate"
                >
                  {liveUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </a>
              </div>
              {!isReallyLive && (
                <div className="text-[12px] text-amber-700 mt-1.5 flex items-center gap-1.5">
                  <Clock className="h-3 w-3" />
                  Staging — connect a custom domain to take it live
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <CopyButton text={liveUrl} />
              <Button asChild variant="outline" size="md">
                <a href={liveUrl} target="_blank" rel="noreferrer">
                  <ExternalLink className="h-3.5 w-3.5" />
                  Visit
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ejected — gated on ejectedAt (NOT the URL): a project ejected before
          migration 0020 has ejectedAt but may have a backfilled URL from the
          engine. Prominently shows the status, repo link, and clone command. */}
      {state.project.ejectedAt && (
        <Card className="border-emerald-300 bg-emerald-50/50">
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 shrink-0 rounded-full bg-emerald-600 flex items-center justify-center">
                <Rocket className="h-4 w-4 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-emerald-900 tracking-tightish text-[15px]">
                    Ejected to your workspace
                  </h3>
                  <span className="text-[10.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-600 text-white">
                    Ejected
                  </span>
                </div>
                <p className="text-[13px] text-emerald-800/90 mt-0.5 leading-relaxed">
                  You manage this site now — the Factory no longer builds it. Ejected{' '}
                  {formatRelative(state.project.ejectedAt)}. Clone it locally to manage
                  it with your AI agent.
                </p>
              </div>
            </div>

            {state.project.ejectedRepoUrl && (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-emerald-700/80">
                    Repository
                  </div>
                  <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-3 py-2">
                    <a
                      href={state.project.ejectedRepoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-[13px] text-emerald-900 hover:underline flex-1 truncate"
                    >
                      {state.project.ejectedRepoUrl.replace(/^https?:\/\//, '')}
                    </a>
                    <CopyButton text={state.project.ejectedRepoUrl} />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-emerald-700/80">
                    <Terminal className="h-3 w-3" />
                    Set up locally
                  </div>
                  <div className="flex items-start gap-2 rounded-lg border border-zinc-200 bg-zinc-950 px-3 py-2.5">
                    <code className="font-mono text-[12.5px] leading-relaxed text-zinc-100 flex-1 whitespace-pre-wrap break-all">
                      {cloneCommand(state.project.ejectedRepoUrl)}
                    </code>
                    <CopyButton text={cloneCommand(state.project.ejectedRepoUrl)} />
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button asChild size="md">
                    <a href={state.project.ejectedRepoUrl} target="_blank" rel="noreferrer">
                      <ExternalLink className="h-3.5 w-3.5" />
                      View on GitHub
                    </a>
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Logo widget — operator can swap the brand logo at any time. New
          logo only affects future builds. */}
      <LogoCard
        projectId={projectId}
        brandId={state.project.brandId}
        branding={state.branding}
        buildRun={state.buildRun}
      />

      {/* Acceptance CTA */}
      {buildIsDone && !accepted && (
        <Card className="border-amber-200 bg-amber-50/40">
          <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 shrink-0 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center">
                <Eye className="h-4 w-4 text-amber-800" />
              </div>
              <div>
                <h3 className="font-semibold text-amber-900 tracking-tightish text-[14.5px]">
                  Review your site
                </h3>
                <p className="text-[13px] text-amber-800/90 mt-0.5 leading-relaxed">
                  Preview the staging URL, review content and images, then approve to enable custom domain connection.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:shrink-0 sm:flex-row flex-col-reverse">
              <Button asChild variant="outline" size="md">
                <a
                  href={`https://${state.project.brandId}.sites.siteskick.com/`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Preview
                </a>
              </Button>
              <Button
                onClick={() => void handleAccept()}
                loading={accepting}
                size="md"
                leftIcon={<Check className="h-3.5 w-3.5" />}
              >
                Approve site
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Connect custom domain CTA */}
      {needsCustomDomain && (
        <Card className="border-blue-200 bg-blue-50/40">
          <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 shrink-0 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center">
                <Globe className="h-4 w-4 text-blue-700" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900 tracking-tightish text-[14.5px]">
                  Ready to go live
                </h3>
                <p className="text-[13px] text-blue-800/90 mt-0.5 leading-relaxed">
                  Connect your custom domain to publish on the web.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:shrink-0">
              <Button variant="outline" size="md" onClick={onGoToDomains}>
                Manage domains
              </Button>
              <Button onClick={() => setPromoteOpen(true)} size="md">
                Connect domain
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Publish & Eject CTA — decoupled from custom domains. Available as soon
          as the site is approved and has a live baseline (default subdomain is
          fine); a custom domain can be attached before or after. Hidden once
          ejected (the Ejected card takes over). */}
      {buildIsDone && accepted && state.project.liveDeploymentId && !state.project.ejectedAt && (
        <Card className="border-zinc-200">
          <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 shrink-0 rounded-full bg-zinc-900 flex items-center justify-center">
                <Rocket className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-zinc-900 tracking-tightish text-[14.5px]">
                  Hand off to your workspace
                </h3>
                <p className="text-[13px] text-zinc-600 mt-0.5 leading-relaxed">
                  Publish this site as your own standalone GitHub repo you manage
                  with your AI agent. No custom domain required — attach one anytime.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:shrink-0">
              <Button
                onClick={() => setEjectOpen(true)}
                size="md"
                leftIcon={<Rocket className="h-3.5 w-3.5" />}
              >
                Publish &amp; Eject
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <PromoteDialog
        projectId={projectId}
        open={promoteOpen}
        onClose={() => setPromoteOpen(false)}
        onPromoted={() => qc.invalidateQueries({ queryKey: ['projectState', projectId] })}
      />

      <EjectDialog
        projectId={projectId}
        open={ejectOpen}
        onClose={() => setEjectOpen(false)}
        onEjected={() => qc.invalidateQueries({ queryKey: ['projectState', projectId] })}
      />

      {/* Build card */}
      <Card>
        <CardHeader>
          <CardTitle>
            {buildIsActive
              ? isMetadataOnly
                ? 'Connecting your custom domain'
                : 'Generating your site'
              : 'Latest build'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!br ? (
            <div className="text-[13px] text-zinc-500">No build yet.</div>
          ) : (
            <div className="flex flex-col gap-5">
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Stat label="Status" value={<StatusBadge status={br.status} />} />
                {version != null && (
                  <Stat label="Version" value={`#${version}`} />
                )}
                <Stat
                  label="Started"
                  value={new Date(br.startedAt).toLocaleString()}
                />
                <Stat
                  label={br.completedAt ? 'Completed' : 'Elapsed'}
                  value={
                    br.completedAt
                      ? formatRelative(br.completedAt)
                      : formatElapsed(br.startedAt)
                  }
                />
              </dl>

              {buildIsActive && (
                <div>
                  <div className="text-[11.5px] uppercase tracking-wide text-zinc-400 mb-2">
                    Progress
                  </div>
                  <BuildProgress
                    status={br.status}
                    lastCompletedPhase={br.lastCompletedPhase}
                    renderMode={br.renderMode}
                  />
                </div>
              )}

              {br.errorMessage && br.status === 'failed' && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-[12.5px] text-red-800 font-mono whitespace-pre-wrap break-words">
                  {br.errorMessage}
                </div>
              )}
            </div>
          )}
        </CardContent>
        {br?.snapshotId && br.status === 'completed' && (
          <CardFooter className="flex flex-wrap justify-end gap-2">
            <DownloadSnapshotButton projectId={projectId} snapshotId={br.snapshotId} />
          </CardFooter>
        )}
      </Card>

      {/* Internal QA validation findings are intentionally NOT surfaced in the
          customer UI. Phase 4 reframe (2026-05) — the deploy gate is gone:
          ValidationReport is metadata-only (saved for developer SQL queries),
          and Screaming Frog (auto-enqueued post-deploy and persisted to
          AuditIssue rows) is the source of truth for live-site SEO issues.
          Neither is surfaced to the customer. The developer reads them via
          SQL and fixes root causes at the engine/template/prompt layer. */}

      {/* Indexing button surface for non-live but completed sites */}
      {buildIsDone && !isReallyLive && (
        <div className="flex justify-end pt-2">
          <IndexButton
            projectId={projectId}
            disabled
            disabledReason="Connect a custom domain before submitting to Google"
            buildRun={state.buildRun}
          />
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-zinc-400 mb-1">
        {label}
      </div>
      <div className="text-[13px] font-medium text-zinc-900">{value}</div>
    </div>
  );
}

function formatElapsed(startIso: string): string {
  const ms = Date.now() - new Date(startIso).getTime();
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label="Copy URL"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* noop */
        }
      }}
      className={cn(copied && 'text-emerald-600')}
    >
      {copied ? <CheckCheck className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

function DownloadSnapshotButton({
  projectId,
  snapshotId,
}: {
  projectId: string;
  snapshotId: string;
}) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const handle = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.downloadSnapshot(projectId, snapshotId);
    } catch (err) {
      toast.error(`Download failed: ${String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handle}
      loading={busy}
      leftIcon={<Download className="h-3 w-3" />}
      title="Download a portable copy of your site"
    >
      Download .tar.gz
    </Button>
  );
}
